import {
  Avatar,
  Box,
  Divider,
  Menu,
  Paper,
  useMediaQuery,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import { useTheme } from '@material-ui/core/styles'
import { useHistory, useParams } from 'react-router-dom'

import { format } from 'date-fns'
import { User } from '../../types/User'
import { useUser } from '../../redux/slices/userSlice'
import { useDispatch } from 'react-redux'
import { deleteDoc, doc, getDoc } from 'firebase/firestore'
import { dbService } from '../../utils/fbase'
import { getSuggestionCollectionId } from '../../utils/dbUtils'

import { Suggestion } from '../../types/Suggestion'
import IconButton from '@material-ui/core/IconButton'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined'
import Loading from '../common/Loading'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { ToastActions } from '../../redux/slices/toastSlice'
import { makeShort } from '../../utils/stringUtils'

import { LocationActions } from '../../redux/slices/locationSlice'
import { SuggestionActions } from '../../redux/slices/suggestionSlice'

export default function SuggestionEnd(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const user: User | null | undefined = useUser()
  const theme = useTheme()
  const matchesSM: boolean = useMediaQuery(theme.breakpoints.down('sm'))
  const dispatch = useDispatch()
  const history = useHistory()
  const [suggestion, setSuggestion] = useState<Suggestion | undefined | null>(
    undefined
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  useEffect(() => {
    if (!user) {
      return
    }

    const docRef = doc(dbService, getSuggestionCollectionId(), id)

    getDoc(docRef).then((data) => {
      if (data.exists()) {
        const tmp: Suggestion = {
          id: data.id,
          author: data.data().author || { displayName: 'Unknown', thumb: '' },
          text: data.data().text,
          timestamp: data.data().timestamp,
          userId: data.data().userId,
          views: data.data().views,
          likes: data.data().likes,
          comments: data.data().comments,
          isConfirmed: data.data().isConfirmed,
        }
        setSuggestion(tmp)
      }
    })
  }, [id, user])

  if (suggestion === undefined) {
    return <Loading />
  }

  if (suggestion === null) {
    return <Loading />
  }

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const handleModify = (e: React.MouseEvent<HTMLElement>) => {
    handleClose(e)
    history.push(`/suggestion/edit/${suggestion?.id}`)
  }

  const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
    if (!user || !suggestion || !suggestion.id) {
      return
    }
    dispatch(
      ToastActions.openDialog({
        title: 'Would you like to delete this?',
        handleOkButton: () => {
          handleClose(event)
          deleteDoc(
            doc(dbService, getSuggestionCollectionId(), suggestion.id || '')
          )
            .then(() => {
              dispatch(
                ToastActions.set({
                  text: `${makeShort(suggestion.text, 10)} has been deleted.`,
                })
              )
              dispatch(SuggestionActions.remove(suggestion.id || ''))
              dispatch(LocationActions.set({ path: '/suggestion' }))
            })
            .catch((error) =>
              console.error(
                `[suggestion] delete error. suggestionId: ${
                  suggestion.id || ''
                }. error: `,
                error
              )
            )
        },
      })
    )
  }

  const date = new Date().getTime()
  return (
    <Paper square>
      <Box p={matchesSM ? 2 : 4}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Avatar
              alt={suggestion.author.displayName}
              src={suggestion.author.thumb}
              style={{ verticalAlign: 'middle' }}
            />
            <div
              style={{
                paddingLeft: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle1">
                {suggestion.author.displayName}
              </Typography>
              <Typography variant="caption">
                {format(date, 'MMM dd, yyyy')}
              </Typography>
            </div>
          </div>
          {user?.id === suggestion.userId && (
            <div>
              <IconButton
                edge="end"
                aria-label="comments"
                onClick={(e) => handleMenu(e)}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
              >
                <MenuItem onClick={(e) => handleModify(e)}>
                  <ListItemIcon style={{ minWidth: '36px' }}>
                    <CreateOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Modify
                </MenuItem>

                <MenuItem onClick={(e) => handleDelete(e)}>
                  <ListItemIcon style={{ minWidth: '36px', color: '#DB3A29' }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <span style={{ color: '#DB3A29' }}>Delete</span>
                </MenuItem>
              </Menu>
            </div>
          )}
        </div>
      </Box>
      <Box p={1}>
        <Divider
          light
          style={{
            marginLeft: matchesSM ? '0.5rem' : '1.5rem',
            marginRight: matchesSM ? '0.5rem' : '1.5rem',
          }}
        />
      </Box>
      <Box p={matchesSM ? 2 : 4}>
        <div style={{ marginTop: '1rem', marginBottom: '2rem' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
            {suggestion.text}
          </Typography>
        </div>
      </Box>
    </Paper>
  )
}
